import { inject, Injectable } from '@angular/core';
import { assert } from '@sama/js-core';
import { EventTrackingService } from '../../../../shared/event-tracking/event-tracking.service';
import { ShapeFeedback } from '../../../../team-apis/otterdam/feedback';
import { PutShapeFeedbackDtoAttributes } from '../../../../team-apis/otterdam/feedback-operations/shape-feedback-operations';
import { FeedbackPersistenceService } from '../../../feedback/services/feedback-persistence.service';
import { TaskWorkspaceStore } from '../../../h3ml/state/task-workspace-store';
import { SamplingWorkspaceStore } from '../../../sampling-workspace/state/sampling-workspace-store';
import { FeedbackListService } from '../feedback-list.service';
import { AbstractFeedbackCrudService } from './abstract-feedback-crud.service';

@Injectable()
export class ShapeFeedbackCrudService extends AbstractFeedbackCrudService<ShapeFeedback> {
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private feedbackListService = inject(FeedbackListService);
  private samplingWorkspaceStore = inject(SamplingWorkspaceStore);
  private eventTrackingService = inject(EventTrackingService);
  private feedbackPersistenceService = inject(FeedbackPersistenceService);

  createUpdateFeedback(
    outputId: number,
    feedbackId: string,
    attributes: PutShapeFeedbackDtoAttributes,
  ) {
    const isNewFeedback = !this.getFeedback(feedbackId);

    this.feedbackPersistenceService.upsertShapeFeedback$(
      outputId,
      feedbackId,
      attributes,
    );
    this.addOrUpdateShapeFeedback(outputId, feedbackId, attributes);
    this.eventTrackingService.track({
      id: isNewFeedback ? 'add_feedback_workspace' : 'edit_feedback_workspace',
      bucket: 'quality_feedback',
      type: 'shape',
      error_tag: attributes.errorTag,
      comment: attributes.comment,
      feedback_id: feedbackId,
      output_id: outputId,
    });
  }

  override delete(feedbackId: string, shapeId?: number): void {
    this.feedbackPersistenceService.deleteShapeFeedback$(
      this.outputInfoStore.state().output.id,
      feedbackId,
      shapeId,
    );
    const removedFeedback = this.getFeedback(feedbackId);
    assert(removedFeedback, 'Feedback to delete not found');
    this.removeFeedback(feedbackId);
    this.eventTrackingService.track({
      id: 'delete_feedback_workspace',
      bucket: 'quality_feedback',
      type: 'shape',
      error_tag: removedFeedback.errorTag,
      comment: removedFeedback.comment,
      feedback_id: removedFeedback.feedbackId,
      output_id: removedFeedback.outputId,
    });
  }

  override getFeedback(id: string) {
    const feedback = this.taskWorkspaceStore.state().shapeFeedback;
    return feedback.find(({ feedbackId }) => feedbackId === id);
  }

  override getFeedbackOutputId(feedback: ShapeFeedback) {
    return feedback.outputId;
  }

  override getFeedbackCoordinates(feedback: ShapeFeedback) {
    return feedback.coordinates;
  }

  private addOrUpdateShapeFeedback(
    outputId: number,
    feedbackId: string,
    attributes: PutShapeFeedbackDtoAttributes,
  ) {
    const currentFeedback = this.taskWorkspaceStore.state().shapeFeedback;
    let updatedFeedback: ShapeFeedback[];
    const newFeedback: ShapeFeedback = {
      outputId,
      feedbackId,
      ...attributes,
      updatedAt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      itemType: 'shape_feedback',
    };
    if (this.getFeedback(feedbackId)) {
      updatedFeedback = currentFeedback.map((fb) =>
        fb.feedbackId === feedbackId ? newFeedback : fb,
      );
    } else {
      updatedFeedback = [...currentFeedback, newFeedback];
      this.samplingWorkspaceStore.updateFeedbackCount(1);
    }
    this.taskWorkspaceStore('shapeFeedback').set(updatedFeedback);
  }

  private removeFeedback(feedbackId: string) {
    // the selection change will trigger the deletion of the
    // missing shape marker
    this.taskWorkspaceStore('shapeFeedback').setUsing((feedback) =>
      feedback.filter((fb) => fb.feedbackId !== feedbackId),
    );
    this.feedbackListService.clearSelection();
    this.samplingWorkspaceStore.updateFeedbackCount(-1);
  }
}
